import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLoginContext } from "../../../App";
import { fetchUserDataByUUID, updateLoggedUser } from "../../../services/b2b-users-apiService";
import { showNotification } from "../../b2b-common/Notification";
import { useContext, useEffect, useState } from "react";
import useB2BTranslation from "../../b2b-common/hooks/useB2BTranslation";
import { B2BContentContext } from "../../layout/Layout";

const useGetUserData = (loadFactor) => {
    const { loginData, myOrganizations } = useLoginContext();
    const { setNotification } = useContext(B2BContentContext);
    const [refresh, setRefresh] = useState(false);
    const [fetchingUserData, setFetchingUserData] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const queryClient = useQueryClient();
    const { t } = useB2BTranslation('b2b-users');

    const fetchUserData = async (loadFactor) => {
        try {
            setFetchingUserData(true);
            const response = await fetchUserDataByUUID(loginData.userUuid, loadFactor, loginData.accessToken);
            return response;
        } catch (error) {
            console.error('Error fetching user data: ' + error.message);
            showNotification(setNotification, t('Error fetching user data'), 'error', 5000);
        } finally {
            setFetchingUserData(false);
        }
    }

    const saveUserData = async (userData) => {
        try {
            setIsSaving(true);
            const response = await updateLoggedUser(userData, loginData.accessToken);
            showNotification(setNotification, t('User data saved successfully'), 'success', 3000);
            return response;
        } catch (error) {
            console.error('Error saving user data: ' + error.message);
            showNotification(setNotification, t('Error saving user data'), 'error', 5000);
        } finally {
            setIsSaving(false);
        }
    }

    const { data: userData } = useQuery({
        queryKey: ['userData', loadFactor, loginData.userUuid],
        queryFn: () => fetchUserData(loadFactor),
        enabled: !!loginData?.userUuid,
    });

    useEffect(() => {
        if (refresh) {
            // console.log('Refreshing user data...');
            queryClient.invalidateQueries(['userData', loadFactor, loginData.userUuid]);
            setFetchingUserData(true);
            setRefresh(false); 
        }
    }, [refresh, queryClient, loginData.userUuid]);

    const triggerRefresh = () => {
        setRefresh(true);
    }

    return { userData, fetchingUserData, myOrganizations, saveUserData, isSaving, triggerRefresh };
}

export default useGetUserData;